import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuthStore } from "@/stores/auth-store";
import { STRINGS } from "@/constants/strings";
import { LoaderCircleIcon, ShieldCheckIcon } from "lucide-react";

export default function MfaChallengePage() {
  const { confirmMfaCode } = useAuthContext();
  const error = useAuthStore((s) => s.error);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const code = (form.elements.namedItem("mfaCode") as HTMLInputElement).value.trim();

    setIsSubmitting(true);
    try {
      await confirmMfaCode(code);
    } catch {
      // error set in store
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
                <ShieldCheckIcon className="size-6 text-primary" />
              </div>
              <h1 className="text-xl font-bold">
                {STRINGS.auth.mfaChallengeTitle}
              </h1>
              <FieldDescription>
                {STRINGS.auth.mfaChallengeDescription}
              </FieldDescription>
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Field>
              <FieldLabel htmlFor="mfaCode">
                {STRINGS.auth.mfaCodeLabel}
              </FieldLabel>
              <Input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder={STRINGS.auth.mfaCodePlaceholder}
                autoComplete="one-time-code"
                autoFocus
                required
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <LoaderCircleIcon className="animate-spin" />
                    {STRINGS.auth.mfaVerifying}
                  </>
                ) : (
                  STRINGS.auth.mfaVerifyButton
                )}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </div>
    </div>
  );
}
